import { useRef, useState } from 'react';
import cn from 'bem-cn';

import { Navigation } from '../../Navigation/Navigation';
import { NavigationElements } from '../../Navigation';
import { Icon } from '../../UI/Icon';
import { useClickOutside } from '../../../hooks';
import { CategorySelectorItem } from '../CategorySelectorItem';

import { CategorySelectorMenuProps } from './CategorySelectorMenu.types';
import './CategorySelectorMenu.scss';

const b = cn('category-selector-menu');

export const CategorySelectorMenu = ({
    categories,
    checkedCategories,
    isOpen,
    onCategoryToggle,
    onClose,
    onClearAll,
    className,
    triggerRef,
}: CategorySelectorMenuProps) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(null);

    useClickOutside({
        ref: menuRef,
        isActive: isOpen,
        onClickOutside: onClose,
        excludeRefs: triggerRef ? [triggerRef] : undefined,
    });

    const handleBackButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClose();
    };

    const navigationElements: NavigationElements = {
        desktop: [],
        mobile: [{ text: 'Применить', onClick: onClose, accent: true }],
    };

    const hasCheckedFromCategories = categories.some(category => checkedCategories.includes(category.id));

    if (!isOpen) {
        return null;
    }

    return (
        <div className={b({ open: isOpen }).mix(className)} ref={menuRef}>
            <div className={b('header')}>
                <div className={b('back-button')} onClick={handleBackButtonClick}>
                    <Icon name="backArrow" size={24} fillColor="#000" />
                </div>
                <h3 className={b('title')}>Категории</h3>
                <span
                    className={b('clear-button', {
                        visible: hasCheckedFromCategories && Boolean(onClearAll),
                    })}
                    onClick={onClearAll}
                >
                    <span className={b('clear-button', 'text')}>Очистить</span>
                    <span className={b('clear-button', 'icon')}>
                        <Icon name="reset" size={20} fillColor="#f55" />
                    </span>
                </span>
            </div>
            <div className={b('content')}>
                {categories.map(category => {
                    const isChecked = checkedCategories.includes(category.id);
                    const isHovered = hoveredCategoryId === category.id;

                    return (
                        <div
                            key={category.id}
                            className={b('item', { checked: isChecked, hovered: isHovered })}
                            onClick={() => onCategoryToggle(category.id)}
                            onMouseEnter={() => setHoveredCategoryId(category.id)}
                            onMouseLeave={() => setHoveredCategoryId(null)}
                        >
                            <div className={b('item-content')}>
                                <CategorySelectorItem
                                    name={category.name}
                                    image={category.image}
                                    video={category.video}
                                    size="normal"
                                    variant="menu"
                                    checked={false}
                                    forceHover={isHovered}
                                />
                            </div>
                            <div className={b('checkbox', { checked: isChecked })}>
                                {isChecked && (
                                    <Icon
                                        name="checkboxTick"
                                        className={b('checkbox-icon')}
                                        strokeColor="#fff"
                                        size={12}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <Navigation elements={navigationElements} />
        </div>
    );
};
